import React from 'react';
import { Search, Filter, X, Calendar, Settings, Target, User, Activity, Clock, Gauge, ThumbsUp, ThumbsDown, Zap, Map } from 'lucide-react';
import { DateRangePicker } from './date-range-picker';
import { MultiSelect } from './multi-select';
import { RangeFilter } from './range-filter';
import { Button } from './button';
import { StatusFilter } from './StatusFilter';
import { getParameterUnitSuffix } from '../../utils/unitUtils';
import { formatHeatInputRangeLabel, HEAT_INPUT_DISPLAY_NAME } from '../../utils/weldParameterLabels';

interface WeldPassDetailFiltersProps {
  // Search filters for fixed columns
  eventFilter: string[];
  onEventFilterChange: (value: string[]) => void;
  statusFilter: string[];
  onStatusFilterChange: (value: string[]) => void;
  jobNumberFilter?: string[];
  onJobNumberFilterChange?: (value: string[]) => void;
  tiltFilter: { min: string; max: string };
  onTiltFilterChange: (value: { min: string; max: string }) => void;
  zoneFilter: string[];
  onZoneFilterChange: (value: string[]) => void;
  /** When false, hides Zone filter (Manual welding has no Zone concept). Default true. */
  showZoneFilter?: boolean;
  
  // Parameter filters for scrollable columns
  voltageFilter: { min: string; max: string };
  onVoltageFilterChange: (value: { min: string; max: string }) => void;
  currentFilter: { min: string; max: string };
  onCurrentFilterChange: (value: { min: string; max: string }) => void;
  travelSpeedFilter: { min: string; max: string };
  onTravelSpeedFilterChange: (value: { min: string; max: string }) => void;
  wireSpeedFilter: { min: string; max: string };
  onWireSpeedFilterChange: (value: { min: string; max: string }) => void;
  heatFilter: { min: string; max: string };
  onHeatFilterChange: (value: { min: string; max: string }) => void;
  
  // Date range filter
  dateRange: { dateFrom: Date | undefined; dateTo: Date | undefined };
  onDateRangeChange: (value: { dateFrom: Date | undefined; dateTo: Date | undefined }) => void;
  
  // Options for dropdowns
  eventOptions: string[];
  statusOptions: string[];
  jobNumberOptions?: string[];
  zoneOptions: string[];
  
  // New props for count display
  totalCount: number;
  filteredCount: number;
}

export const WeldPassDetailFilters: React.FC<WeldPassDetailFiltersProps> = ({
  eventFilter,
  onEventFilterChange,
  statusFilter,
  onStatusFilterChange,
  jobNumberFilter,
  onJobNumberFilterChange,
  tiltFilter,
  onTiltFilterChange,
  zoneFilter,
  onZoneFilterChange,
  showZoneFilter = true,
  voltageFilter,
  onVoltageFilterChange,
  currentFilter,
  onCurrentFilterChange,
  travelSpeedFilter,
  onTravelSpeedFilterChange,
  wireSpeedFilter,
  onWireSpeedFilterChange,
  heatFilter,
  onHeatFilterChange,
  dateRange,
  onDateRangeChange,
  eventOptions,
  statusOptions,
  jobNumberOptions,
  zoneOptions,
  totalCount,
  filteredCount
}) => {
  const clearAllFilters = () => {
    onEventFilterChange([]);
    onStatusFilterChange([]);
    onJobNumberFilterChange?.([]);
    onTiltFilterChange({ min: '', max: '' });
    onZoneFilterChange([]);
    onVoltageFilterChange({ min: '', max: '' });
    onCurrentFilterChange({ min: '', max: '' });
    onTravelSpeedFilterChange({ min: '', max: '' });
    onWireSpeedFilterChange({ min: '', max: '' });
    onHeatFilterChange({ min: '', max: '' });
    onDateRangeChange({ dateFrom: undefined, dateTo: undefined });
  };

  const hasActiveFilters = eventFilter.length > 0 || statusFilter.length > 0 || (jobNumberFilter && jobNumberFilter.length > 0) || 
    (tiltFilter.min || tiltFilter.max) || (showZoneFilter && zoneFilter.length > 0) || 
    (voltageFilter.min || voltageFilter.max) || (currentFilter.min || currentFilter.max) || 
    (travelSpeedFilter.min || travelSpeedFilter.max) || (wireSpeedFilter.min || wireSpeedFilter.max) || 
    (heatFilter.min || heatFilter.max) || dateRange.dateFrom || dateRange.dateTo;

  // Convert string arrays to option arrays for MultiSelect
  const eventOptionsFormatted = eventOptions.map(option => ({ value: option, label: option }));
  const jobNumberOptionsFormatted = jobNumberOptions?.map(option => ({ value: option, label: option })) || [];
  const zoneOptionsFormatted = zoneOptions.map(option => ({ value: option, label: option }));

  // Get dynamic unit labels for filter components
  const getUnitLabel = (parameter: string): string => {
    return getParameterUnitSuffix(parameter);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="font-medium text-gray-700 text-xs">Weld Pass Detail Filters</span>
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-4 w-4 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      {/* Filters Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {/* Event Filter */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Event
          </label>
          <MultiSelect
            options={eventOptionsFormatted}
            selected={eventFilter}
            onChange={onEventFilterChange}
            placeholder="Select events"
            className="w-full"
          />
        </div>

        {/* Status Filter */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Status
          </label>
          <StatusFilter
            selected={statusFilter}
            onChange={onStatusFilterChange}
            placeholder="Select status"
            className="w-full"
          />
        </div>

        {/* Job Number Filter - Only show if props are provided */}
        {jobNumberFilter !== undefined && onJobNumberFilterChange && jobNumberOptions && (
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
              <Target className="h-4 w-4" />
              Job Number
            </label>
            <MultiSelect
              options={jobNumberOptionsFormatted}
              selected={jobNumberFilter}
              onChange={onJobNumberFilterChange}
              placeholder="Select job numbers"
              className="w-full"
            />
          </div>
        )}

        {/* Tilt Range Filter */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Gauge className="h-4 w-4" />
            Tilt Range
          </label>
          <RangeFilter
            label=""
            minValue={tiltFilter.min}
            maxValue={tiltFilter.max}
            onMinChange={(value) => onTiltFilterChange({ ...tiltFilter, min: value })}
            onMaxChange={(value) => onTiltFilterChange({ ...tiltFilter, max: value })}
            placeholder="tilt"
            className="mt-2"
          />
        </div>

        {/* Zone Filter — hidden for Manual (no Zone concept) */}
        {showZoneFilter && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Map className="h-4 w-4" />
            Zone
          </label>
          <MultiSelect
            options={zoneOptionsFormatted}
            selected={zoneFilter}
            onChange={onZoneFilterChange}
            placeholder="Select zones"
            className="w-full"
          />
        </div>
        )}

        {/* Voltage Range Filter */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Voltage Range ({getUnitLabel('volts')})
          </label>
          <RangeFilter
            label=""
            minValue={voltageFilter.min}
            maxValue={voltageFilter.max}
            onMinChange={(value) => onVoltageFilterChange({ ...voltageFilter, min: value })}
            onMaxChange={(value) => onVoltageFilterChange({ ...voltageFilter, max: value })}
            placeholder="voltage"
            unit={getUnitLabel('volts')}
            className="mt-2"
          />
        </div>

        {/* Current Range Filter */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Current Range ({getUnitLabel('current')})
          </label>
          <RangeFilter
            label=""
            minValue={currentFilter.min}
            maxValue={currentFilter.max}
            onMinChange={(value) => onCurrentFilterChange({ ...currentFilter, min: value })}
            onMaxChange={(value) => onCurrentFilterChange({ ...currentFilter, max: value })}
            placeholder="current"
            unit={getUnitLabel('current')}
            className="mt-2"
          />
        </div>

        {/* Travel Speed Range Filter */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Gauge className="h-4 w-4" />
            Travel Speed Range ({getUnitLabel('travel_speed')})
          </label>
          <RangeFilter
            label=""
            minValue={travelSpeedFilter.min}
            maxValue={travelSpeedFilter.max}
            onMinChange={(value) => onTravelSpeedFilterChange({ ...travelSpeedFilter, min: value })}
            onMaxChange={(value) => onTravelSpeedFilterChange({ ...travelSpeedFilter, max: value })}
            placeholder="travel speed"
            unit={getUnitLabel('travel_speed')}
            className="mt-2"
          />
        </div>

        {/* Wire Speed Range Filter */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Gauge className="h-4 w-4" />
            Wire Speed Range ({getUnitLabel('wire_speed')})
          </label>
          <RangeFilter
            label=""
            minValue={wireSpeedFilter.min}
            maxValue={wireSpeedFilter.max}
            onMinChange={(value) => onWireSpeedFilterChange({ ...wireSpeedFilter, min: value })}
            onMaxChange={(value) => onWireSpeedFilterChange({ ...wireSpeedFilter, max: value })}
            placeholder="wire speed"
            unit={getUnitLabel('wire_speed')}
            className="mt-2"
          />
        </div>

        {/* Heat Range Filter */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Zap className="h-4 w-4" />
            {formatHeatInputRangeLabel(getUnitLabel('heat'))}
          </label>
          <RangeFilter
            label=""
            minValue={heatFilter.min}
            maxValue={heatFilter.max}
            onMinChange={(value) => onHeatFilterChange({ ...heatFilter, min: value })}
            onMaxChange={(value) => onHeatFilterChange({ ...heatFilter, max: value })}
            placeholder="heat"
            unit={getUnitLabel('heat')}
            className="mt-2"
          />
        </div>

        {/* Date Range Filter */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Date Range
          </label>
          
          <DateRangePicker
            initialDateFrom={dateRange.dateFrom}
            initialDateTo={dateRange.dateTo}
            onUpdate={(values) => onDateRangeChange({
              dateFrom: values.dateFrom,
              dateTo: values.dateTo
            })}
            showCompare={false}
            className="w-full"
          />
        </div>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            {eventFilter.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-blueprint-blue bg-[rgba(0,39,63,0.06)] text-xs rounded-full">
                Event: {eventFilter.join(', ')}
                <button
                  onClick={() => onEventFilterChange([])}
                  className="ml-1 hover:text-blue-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {statusFilter.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-galvanizing-green bg-[rgba(31,154,58,0.08)] text-xs rounded-full">
                Status: {statusFilter.join(', ')}
                <button
                  onClick={() => onStatusFilterChange([])}
                  className="ml-1 hover:text-green-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {jobNumberFilter && jobNumberFilter.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-blueprint-blue bg-[rgba(0,39,63,0.06)] text-xs rounded-full">
                Job Number: {jobNumberFilter.join(', ')}
                <button
                  onClick={() => onJobNumberFilterChange?.([])}
                  className="ml-1 hover:text-purple-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {(tiltFilter.min || tiltFilter.max) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-[#B38600] bg-[rgba(230,161,0,0.08)] text-xs rounded-full">
                Tilt: {tiltFilter.min || '0'} - {tiltFilter.max || '∞'}
                <button
                  onClick={() => onTiltFilterChange({ min: '', max: '' })}
                  className="ml-1 hover:text-orange-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {showZoneFilter && zoneFilter.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-800 text-xs rounded-full">
                Zone: {zoneFilter.join(', ')}
                <button
                  onClick={() => onZoneFilterChange([])}
                  className="ml-1 hover:text-teal-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {(voltageFilter.min || voltageFilter.max) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-readable-red bg-[rgba(196,0,31,0.06)] text-xs rounded-full">
                Voltage: {voltageFilter.min || '0'} - {voltageFilter.max || '∞'}
                <button
                  onClick={() => onVoltageFilterChange({ min: '', max: '' })}
                  className="ml-1 hover:text-pink-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {(currentFilter.min || currentFilter.max) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-readable-red bg-[rgba(196,0,31,0.06)] text-xs rounded-full">
                Current: {currentFilter.min || '0'} - {currentFilter.max || '∞'}
                <button
                  onClick={() => onCurrentFilterChange({ min: '', max: '' })}
                  className="ml-1 hover:text-red-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {(travelSpeedFilter.min || travelSpeedFilter.max) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-[#B38600] bg-[rgba(230,161,0,0.08)] text-xs rounded-full">
                Travel Speed: {travelSpeedFilter.min || '0'} - {travelSpeedFilter.max || '∞'}
                <button
                  onClick={() => onTravelSpeedFilterChange({ min: '', max: '' })}
                  className="ml-1 hover:text-yellow-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {(wireSpeedFilter.min || wireSpeedFilter.max) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-100 text-cyan-800 text-xs rounded-full">
                Wire Speed: {wireSpeedFilter.min || '0'} - {wireSpeedFilter.max || '∞'}
                <button
                  onClick={() => onWireSpeedFilterChange({ min: '', max: '' })}
                  className="ml-1 hover:text-cyan-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {(heatFilter.min || heatFilter.max) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-galvanizing-green bg-[rgba(31,154,58,0.08)] text-xs rounded-full">
                {HEAT_INPUT_DISPLAY_NAME}: {heatFilter.min || '0'} - {heatFilter.max || '∞'}
                <button
                  onClick={() => onHeatFilterChange({ min: '', max: '' })}
                  className="ml-1 hover:text-lime-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {(dateRange.dateFrom || dateRange.dateTo) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                Date Range: {dateRange.dateFrom ? `${dateRange.dateFrom.getDate().toString().padStart(2, '0')}-${(dateRange.dateFrom.getMonth() + 1).toString().padStart(2, '0')}-${dateRange.dateFrom.getFullYear()} ${dateRange.dateFrom.getHours() % 12 || 12}:${dateRange.dateFrom.getMinutes().toString().padStart(2, '0')} ${dateRange.dateFrom.getHours() >= 12 ? 'PM' : 'AM'}` : ''} - {dateRange.dateTo ? `${dateRange.dateTo.getDate().toString().padStart(2, '0')}-${(dateRange.dateTo.getMonth() + 1).toString().padStart(2, '0')}-${dateRange.dateTo.getFullYear()} ${dateRange.dateTo.getHours() % 12 || 12}:${dateRange.dateTo.getHours() >= 12 ? 'PM' : 'AM'}` : ''}
                <button
                  onClick={() => onDateRangeChange({ dateFrom: undefined, dateTo: undefined })}
                  className="ml-1 hover:text-gray-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Pagination Display */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Showing:</span>
            <span className="text-sm text-gray-900 font-semibold">
              {filteredCount > 0 ? `1-${filteredCount}` : '0'} of {totalCount}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}; 